import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, Heart, Shield, Sparkles, Activity, Users, Globe, Zap, Smartphone, TrendingUp, DollarSign, Target, Mic } from 'lucide-react';
import therappiLogo from "@/assets/therappi-logo-cropped.png";
import therappiWhoopLogo from '@/assets/therappi-whoop-combined.svg';
import phoneCheckinMockup from '@/assets/phone-daily-tracking.png';
import amandaCharlesPhoto from '@/assets/amanda-charles-founder.jpeg';
import { Button } from '@/components/ui/button';

const GooglePlayAccelerator = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-background">
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
            background-color: hsl(173 58% 15%);
          }
          
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          html {
            min-height: 100vh;
            background-color: hsl(173 58% 15%) !important;
          }
          
          body {
            margin: 0 !important;
            padding: 0 !important;
            background-color: hsl(173 58% 15%) !important;
          }
          
          .print-page {
            width: 210mm;
            height: 297mm;
            padding: 12mm;
            box-sizing: border-box;
            background-color: hsl(173 58% 15%) !important;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .page-break {
            page-break-after: always;
            break-after: page;
          }
          
          .avoid-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          ::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>

      <div className="fixed top-4 right-4 z-50 print:hidden">
        <Button onClick={handlePrint} className="gap-2">
          <Sparkles className="h-4 w-4" />
          Download as PDF
        </Button>
      </div>

      <div className="max-w-[800px] mx-auto px-8 pb-12 print:max-w-none print:mx-0 print:px-0 print:pb-0">
        
        {/* SLIDE 1 - Cover */}
        <div className="min-h-screen flex flex-col justify-center items-center text-center page-break py-12 print:print-page">
          <div className="w-full max-w-xl mx-auto mb-6">
            <img src={therappiLogo} alt="Therappi®" className="w-full h-auto" />
          </div>
          <h1 className="text-5xl font-bold mb-4 text-primary">
            WHERE SCIENCE MEETS SOUL
          </h1>
          <Badge className="text-lg px-8 py-3 bg-gradient-to-r from-primary to-accent mb-6">
            Google Play App Accelerator Application 2025
          </Badge>
          <p className="text-2xl font-semibold mb-3">
            A Multidimensional Therapeutic AI Companion
          </p>
          <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
            Where Psychology, Quantum Science and Biometric Intelligence Meet
          </p>
        </div>

        {/* SLIDE 2 - What is THERAPPI */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-background via-primary/5 to-accent/5 avoid-break">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="h-8 w-8 text-primary" />
              <h2 className="text-4xl font-bold">
                What is THERAPPI<sup className="text-[0.6rem] align-super">®</sup>?
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-foreground/90 mb-8">
              Therappi<sup className="text-[0.6rem] align-super">®</sup> is an AI-powered therapeutic companion designed to deliver personalised, 
              multidimensional support for mental and emotional wellbeing. Built at the intersection of psychology, quantum science 
              and biometric data, Therappi adapts to the user's patterns, physiology and lived experience in real time.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">Evidence-Based AI Therapy</h3>
                  <p className="text-sm text-muted-foreground">Integrates CBT, DBT, ACT, IFS and trauma-informed modalities</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">Biometric Intelligence</h3>
                  <p className="text-sm text-muted-foreground">WHOOP + Android Sensors: Correlates HRV, recovery, sleep and behavioural patterns</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">QuantumSelf Psychology™ Framework</h3>
                  <p className="text-sm text-muted-foreground">Holistic model spanning physical, emotional, mental, relational and spiritual dimensions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mic className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">Voice Mode</h3>
                  <p className="text-sm text-muted-foreground">Natural therapeutic conversations via voice for deeper connection and accessibility</p>
                </div>
              </div>
              <div className="flex items-start gap-3 md:col-span-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base">Personalised Memory & Context</h3>
                  <p className="text-sm text-muted-foreground">Remembers patterns and history to deliver tailored, predictive therapeutic support</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 3 - The Problem */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-background via-primary/5 to-accent/5 avoid-break">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 text-primary" />
              <h2 className="text-4xl font-bold">The Mental Health Crisis is Escalating</h2>
            </div>
            <div className="space-y-4 text-base leading-relaxed">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Over 1 billion people</strong> experience mental health difficulties (WHO, 2025)
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>70% globally</strong> receive no treatment due to cost, access, or stigma
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  Psychological services remain <strong>overstretched</strong>, with long waiting lists
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Smartphone className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  Current wellness apps offer <strong>generic, non-clinical, one-size-fits-all tools</strong>
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  Users are left managing symptoms alone, <strong>without real-time, personalised support</strong>
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 4 - The Solution */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 avoid-break">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="h-8 w-8 text-primary" />
              <h2 className="text-4xl font-bold">
                The Solution: Therappi<sup className="text-[0.6rem] align-super">®</sup>
              </h2>
            </div>
            <p className="text-2xl font-semibold text-primary mb-6">
              A multidimensional AI therapy companion
            </p>
            <p className="text-base mb-6 leading-relaxed">
              Therappi provides personalised, clinically-informed, psychologically coherent support across every level of the self:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Brain className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Evidence-based AI therapy</h3>
                  <p className="text-xs text-muted-foreground">(CBT, DBT, ACT, IFS, trauma-informed)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Activity className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Biometric intelligence</h3>
                  <p className="text-xs text-muted-foreground">via WHOOP and device sensors</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">QuantumSelf Psychology™ framework</h3>
                  <p className="text-xs text-muted-foreground">integrating mind, body & energy</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Predictive emotional analytics</h3>
                  <p className="text-xs text-muted-foreground">to intervene before crisis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Identity and timeline transformation</h3>
                  <p className="text-xs text-muted-foreground">tools unique to Therappi</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Heart className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Warmth, empathy, clinical competence</h3>
                  <p className="text-xs text-muted-foreground">at scale</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 5 - What Makes Therappi Unique */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
              <Sparkles className="h-8 w-8 text-primary" />
              <span>What Makes Therappi Unique</span>
            </h2>
            <p className="text-2xl text-primary font-semibold mb-2">
              A Whole-System Approach
            </p>
            <p className="text-lg text-muted-foreground">
              Most apps help you "cope".<br />
              <strong>Therappi helps you heal, transform and reprogramme.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 avoid-break">
            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Every Level of the Self</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Integrates physical, emotional, mental, relational, and spiritual dimensions
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">25+ Years Clinical Experience</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                AI trained on extensive clinical, trauma-informed practice
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Inflammatory Load (IL) System</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Proprietary system linking lifestyle + physiology + psychology
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Identity & Timeline Tools</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Uses identity work, timeline mapping, and R-Method™ for lasting change
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Contextual Memory</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Personalises support using patterns and biometric cues
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Real Transformation</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Not just symptom management - fundamental reprogramming
              </p>
            </Card>
          </div>
        </div>

        {/* SLIDE 6 - Why Voice Mode Transforms Therappi */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
              <Mic className="h-8 w-8 text-primary" />
              <span>Why Voice Mode on Android Transforms Therappi<sup className="text-[0.6rem] align-super">®</sup></span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Voice is the future of therapeutic AI. Integrated voice support allows Therappi to deliver warm, empathic, 
              real-time guidance that feels human, accessible and clinically grounded.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 avoid-break">
            <Card className="p-5 print:shadow-none print:border-2">
              <Heart className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Human Connection</h3>
              <p className="text-sm text-muted-foreground">
                Voice interactions create warmth, empathy and trust - essential for therapeutic rapport.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Shield className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Clinical Authority</h3>
              <p className="text-sm text-muted-foreground">
                A psychologically informed voice tone strengthens reassurance during moments of distress.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Users className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Accessibility & Inclusion</h3>
              <p className="text-sm text-muted-foreground">
                Ideal for users with visual impairments, dyslexia, literacy challenges or auditory preferences.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Activity className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Real-Time Support</h3>
              <p className="text-sm text-muted-foreground">
                Hands-free therapeutic guidance while walking, commuting, or managing difficult moments.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Zap className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Emotional Nuance</h3>
              <p className="text-sm text-muted-foreground">
                Tone, pace and emotion detection allow Therappi to respond with sensitivity and precision.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Globe className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Global Reach</h3>
              <p className="text-sm text-muted-foreground">
                Voice mode supports multilingual expansion to serve users across diverse regions.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Heart className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Privacy & Comfort</h3>
              <p className="text-sm text-muted-foreground">
                Voice interactions feel more natural and intimate, reducing stigma and encouraging openness.
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Shield className="h-8 w-8 text-accent mb-3" />
              <h3 className="text-base font-semibold mb-2">Safety & Ethics</h3>
              <p className="text-sm text-muted-foreground">
                Anchored in trauma-informed care, transparent AI safety protocols and responsible design.
              </p>
            </Card>
          </div>
        </div>

        {/* SLIDE 7 - How Therappi Will Use Voice */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
              <Smartphone className="h-8 w-8 text-primary" />
              <span>How Therappi Will Use Voice Technologies</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              A two-phase implementation strategy to integrate voice into every aspect of the therapeutic experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 avoid-break">
            <Card className="p-6 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-bold">Phase 1: Voice-Enhanced AI Therapist</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Conversational Therapy Sessions</h4>
                    <p className="text-xs text-muted-foreground">Users speak directly to AI for warm, responsive, clinically informed dialogue</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Voice Check-Ins</h4>
                    <p className="text-xs text-muted-foreground">Morning/evening check-ins completed hands-free</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Crisis Support</h4>
                    <p className="text-xs text-muted-foreground">Immediate grounding through voice-led breathwork and safety steps</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Guided Audio Exercises</h4>
                    <p className="text-xs text-muted-foreground">Meditations, breathwork, visualisations and somatic tools</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Personalised Affirmations</h4>
                    <p className="text-xs text-muted-foreground">Voice reminders aligned with user goals and patterns</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Voice Journaling</h4>
                    <p className="text-xs text-muted-foreground">Users speak freely; Therappi reflects insights and themes</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 bg-gradient-to-br from-accent/5 to-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent text-accent-foreground rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-bold">Phase 2: Advanced Voice Intelligence</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Emotional Tone Analysis*</h4>
                    <p className="text-xs text-muted-foreground">Identifies stress, anxiety via vocal markers for early intervention</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Multilingual Expansion</h4>
                    <p className="text-xs text-muted-foreground">Voice-first support for global audiences across multiple languages</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Contextual Voice Responses</h4>
                    <p className="text-xs text-muted-foreground">Adjusts tone based on time, patterns, biometric data and goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Voice + Biometric Correlation*</h4>
                    <p className="text-xs text-muted-foreground">Cross-references voice patterns with HRV, sleep and recovery</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Therapeutic Voice Coaching</h4>
                    <p className="text-xs text-muted-foreground">Live guidance during stress points or emotional spikes</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold">Voice-Enabled Goal Tracking</h4>
                    <p className="text-xs text-muted-foreground">Users set and track goals via voice with spoken updates</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-4 pt-3 border-t border-border/50">
                *Vision features require specialised APIs or research partnerships
              </p>
            </Card>
          </div>
        </div>

        {/* SLIDE 8 - Product Features */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3">Comprehensive Product Features</h2>
            <p className="text-lg text-muted-foreground">
              A complete therapeutic ecosystem addressing physical, emotional, mental, relational and spiritual wellbeing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 avoid-break">
            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Therappi<sup className="text-[0.4rem] align-super">®</sup> AI Psychologist</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Conversational AI delivering CBT, DBT, ACT, IFS and trauma-informed interventions
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Intelligent Tracking & Check-Ins</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Mood, sleep, energy, symptoms, menstrual cycle, life events, behavioural trends
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Evidence-Based Coping Tools</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Somatic regulation, breathwork, meditations, worksheets, crisis support, grounding
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Advanced Analytics & Insights</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Predictive emotional analytics across sleep, HRV, recovery, nutrition and patterns
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Holistic Life Tracking</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Nutrition, movement, hydration, appointments, personal goals and life events
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center gap-2 mb-3">
                <Mic className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Voice Mode</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Warm, empathic voice therapy, hands-free check-ins, crisis support and journaling
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Inflammatory Load (IL) System</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Proprietary metric connecting lifestyle, emotional states, physiology and patterns
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-6 w-6 text-primary" />
                <h3 className="text-base font-bold">Timeline Healing & Identity Work</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Life mapping, trauma imprints, narrative reconstruction and future-self alignment
              </p>
            </Card>
          </div>
        </div>

        {/* SLIDE 9 - Biometric Integration */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 avoid-break">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Activity className="h-7 w-7 text-primary" />
                  <h2 className="text-3xl font-bold">Biometric Integration</h2>
                </div>
                <p className="text-base mb-4 leading-relaxed font-semibold text-primary">
                  Therappi is the world's first therapeutic AI platform to integrate WHOOP and device biometrics, 
                  creating a unified mind-body dashboard.
                </p>
                <p className="text-sm mb-3 font-semibold">Tracks:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Heart className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>HRV</strong> - stress, resilience, nervous system regulation</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Activity className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>Recovery Score</strong> - cognitive load & burnout prediction</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>Sleep Performance</strong> - mood instability & circadian alignment</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>Strain</strong> - emotional overload patterns</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Activity className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>Respiratory Rate</strong> - early detection of stress/illness</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Smartphone className="h-4 w-4 text-primary mt-0.5" />
                    <p><strong>Android-compatible sensors</strong> - future expansion</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={therappiWhoopLogo} 
                  alt="Therappi® + WHOOP Integration" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 10 - Why Google Play Accelerator */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
              <Smartphone className="h-8 w-8 text-primary" />
              <span>Why Google Play Accelerator</span>
            </h2>
            <p className="text-xl text-primary font-semibold">
              Google mentorship amplifies Therappi's global mission
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 avoid-break">
            <Card className="p-5 print:shadow-none print:border-2">
              <Globe className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Global Distribution</h3>
              <p className="text-sm text-muted-foreground">
                Android + Google Play distribution to scale worldwide
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Users className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Multilingual Expansion</h3>
              <p className="text-sm text-muted-foreground">
                Support for multilingual expansion + accessibility
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Smartphone className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">WearOS Integration</h3>
              <p className="text-sm text-muted-foreground">
                Optimisation for WearOS, Assistant and device-level features
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Shield className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">AI Safety Standards</h3>
              <p className="text-sm text-muted-foreground">
                Support integrating AI safety, wellbeing standards and clinical ethics
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <TrendingUp className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Growth Strategy</h3>
              <p className="text-sm text-muted-foreground">
                Growth strategy guidance for emerging markets
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Zap className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Performance Optimisation</h3>
              <p className="text-sm text-muted-foreground">
                Performance and UX optimisation across Android devices
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Activity className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Health APIs</h3>
              <p className="text-sm text-muted-foreground">
                Opportunity to expand using Google Health/Wellbeing APIs
              </p>
            </Card>

            <Card className="p-5 print:shadow-none print:border-2">
              <Brain className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-base font-semibold mb-2">Expert Mentorship</h3>
              <p className="text-sm text-muted-foreground">
                Access to Google experts for product, engineering and growth guidance
              </p>
            </Card>
          </div>
        </div>

        {/* SLIDE 11 - Business Model */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 avoid-break">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="h-8 w-8 text-primary" />
              <h2 className="text-4xl font-bold">Business Model</h2>
            </div>
            <p className="text-2xl font-semibold text-primary mb-6">
              B2C | Subscription-Based
            </p>
            <div className="space-y-4 text-base">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Monthly / annual subscription</strong>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Free tier</strong> with limited features
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Premium features</strong> include biometric intelligence, voice mode, and identity/timeline modules
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Optional add-on:</strong> human support for escalations
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <p className="flex-1">
                  <strong>Later expansion:</strong> B2B2C partnerships with clinics, wellness centres & employers
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 12 - Market Opportunity */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <div className="text-center mb-6 avoid-break">
            <h2 className="text-4xl font-bold mb-3 flex items-center justify-center gap-3">
              <TrendingUp className="h-8 w-8 text-primary" />
              <span>Market Opportunity</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Mental health is a global crisis - and Therappi is built to address it at scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6 avoid-break">
            <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/10 to-primary/5">
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">$560B</div>
                <p className="text-base font-semibold">Global mental health market by 2030</p>
              </div>
            </Card>

            <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-accent/10 to-accent/5">
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">$1T</div>
                <p className="text-base font-semibold">Annual economic impact of anxiety & depression</p>
              </div>
            </Card>
          </div>

          <Card className="p-6 print:shadow-none print:border-2 avoid-break">
            <div className="space-y-3 text-base">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                <p><strong>1B+ people</strong> living with mental health challenges</p>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <p><strong>70% treatment gap</strong> in low-middle income regions</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 mt-6 avoid-break">
            <h3 className="text-xl font-bold mb-4 text-primary">Why Therappi's Model Wins</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <p>Always-on, accessible, affordable therapeutic support</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <p>Evidence-based and clinically informed</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <p>Deep personalisation via biometrics</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <p>Human-like connection through voice</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <p>Multidimensional healing across every level of the Self</p>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 13 - The Founder */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 via-primary/5 to-accent/5 avoid-break">
            <div className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-1 flex flex-col items-center text-center">
                <img 
                  src={amandaCharlesPhoto} 
                  alt="Amanda Charles - Founder" 
                  className="w-64 h-64 rounded-2xl shadow-xl mb-4 object-cover"
                />
                <h3 className="text-2xl font-bold mb-2">Amanda Charles</h3>
                <p className="text-base text-primary font-semibold mb-1">
                  CEO and Founder of Therappi<sup className="text-[0.6rem] align-super">®</sup>
                </p>
                <p className="text-sm text-muted-foreground">
                  The Psychic Psychologist<sup className="text-[0.6rem] align-super">®</sup>
                </p>
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-7 w-7 text-primary" />
                  <h2 className="text-3xl font-bold">The Founder</h2>
                </div>
                <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
                  <p>
                    <strong>Amanda Charles, The Psychic Psychologist<sup className="text-[0.4rem] align-super">®</sup></strong>, 
                    is a Chartered Counselling Psychologist registered with the British Psychological 
                    Society (BPS) and the Health and Care Professions Council (HCPC), bringing <strong>25+ years</strong> of extensive therapeutic experience.
                  </p>
                  <p>
                    She is the founder of <strong>QuantumSelf Psychology™</strong>, a paradigm that bridges 
                    science and spirituality by integrating research-based psychological interventions with quantum science, 
                    psycho-neuro-immunology, consciousness studies, and epigenetics.
                  </p>
                  <p>
                    Amanda is an <strong>International Speaker and Author</strong> of the multi-award-winning No.1 international 
                    bestseller <em>The Psychic Psychologist<sup className="text-[0.4rem] align-super">®</sup></em>, 
                    published by Hay House. In 2025, she was awarded the <strong>Nautilus Silver Book Award</strong> in 
                    Psychology/Mental and Emotional Wellbeing - an honour previously given to King Charles III and the Dalai Lama.
                  </p>
                  <p>
                    Her dedication to this mission is deeply personal. Amanda combined these disciplines to recover from the trauma 
                    of suddenly losing every member of her immediate family, navigating marriage breakdown, healing herself from 
                    chronic illnesses, pituitary tumours, infertility, and more recently, a suspected stroke.
                  </p>
                  <p>
                    As an international speaker, Amanda has delivered her teachings at the Flow Summit alongside <strong>Dr Joe Dispenza, 
                    Eckhart Tolle, Deepak Chopra and the Dalai Lama</strong>. She is a regular speaker on the main stage at the Mind Body 
                    Spirit Festival and provides professional training through The School of QuantumSelf Psychology™ and CPD-accredited workshops for PESI Inc.
                  </p>
                  <p className="font-semibold text-primary">
                    Therappi<sup className="text-[0.4rem] align-super">®</sup> is the culmination of Amanda's life's work - bringing 
                    her 25+ years of clinical expertise, quantum science insights, trauma-informed care, and intuitive wisdom into an 
                    AI companion. This scales therapeutic healing to millions worldwide.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* SLIDE 14 - Our Vision */}
        <div className="min-h-screen flex flex-col justify-center page-break py-12 print:print-page">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 text-center avoid-break">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="h-10 w-10 text-primary" />
              <h2 className="text-5xl font-bold">Our Vision</h2>
            </div>
            <p className="text-2xl font-semibold text-primary mb-6 leading-relaxed">
              The world's first therapeutic AI that works across your whole timeline:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <Card className="p-6 print:shadow-none print:border-2 bg-background/50">
                <h3 className="text-xl font-bold text-primary">Healing the past</h3>
              </Card>
              <Card className="p-6 print:shadow-none print:border-2 bg-background/50">
                <h3 className="text-xl font-bold text-primary">Optimising the present</h3>
              </Card>
              <Card className="p-6 print:shadow-none print:border-2 bg-background/50">
                <h3 className="text-xl font-bold text-primary">Shaping the future</h3>
              </Card>
            </div>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              Supporting every level of your being - <strong>physical, emotional, mental, spiritual & relational.</strong>
            </p>
          </Card>
        </div>

        {/* SLIDE 15 - Call to Action */}
        <div className="min-h-screen flex flex-col justify-center py-12 print:print-page">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 text-center avoid-break">
            <div className="mb-8">
              <div className="w-full max-w-md mx-auto mb-6">
                <img src={therappiLogo} alt="Therappi®" className="w-full h-auto" />
              </div>
              <h2 className="text-4xl font-bold mb-4">
                Therappi<sup className="text-[0.6rem] align-super">®</sup> x Google Play Accelerator
              </h2>
              <p className="text-2xl text-primary font-semibold mb-6">
                Scaling mental wellbeing through accessible, empathic, evidence-based AI.
              </p>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
                By integrating voice technology with Amanda Charles' 25+ years of clinical expertise, 
                Therappi will democratise access to world-class therapeutic support for millions worldwide - 
                breaking down barriers of cost, geography, stigma, and accessibility.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-3xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-primary mb-2">Accessible</div>
                <p className="text-sm text-muted-foreground">24/7 support regardless of location or income</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary mb-2">Human-Like</div>
                <p className="text-sm text-muted-foreground">Warm, empathetic voice creating genuine connection</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary mb-2">Evidence-Based</div>
                <p className="text-sm text-muted-foreground">Clinically-grounded interventions at scale</p>
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto text-left">
                <div>
                  <h3 className="font-semibold text-base mb-1 text-primary">Website</h3>
                  <p className="text-sm">www.therappi.app</p>
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1 text-primary">Contact</h3>
                  <p className="text-sm">support@therappi.app</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
};

export default GooglePlayAccelerator;
